import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { formatDate, formatEuro } from '../lib/format';

function fmtM(cm: number): string {
  return `${(cm / 100).toFixed(2).replace('.', ',')} m`;
}

export function ReferenceRouleauxPage(): JSX.Element {
  const { id } = useParams();
  const refId = id !== undefined ? Number.parseInt(id, 10) : NaN;

  const refQuery = useQuery({
    queryKey: ['references', 'detail', refId],
    queryFn: () => api.references.get(refId),
    enabled: !Number.isNaN(refId),
  });

  const rouleauxQuery = useQuery({
    queryKey: ['rouleaux', 'reference', refId],
    queryFn: () => api.rouleaux.list({ reference_id: refId }),
    enabled: !Number.isNaN(refId),
  });

  if (Number.isNaN(refId)) return <div className="text-sm text-red-400">ID invalide.</div>;

  return (
    <div className="max-w-7xl">
      <header className="flex items-end justify-between mb-6">
        <div>
          <div className="text-xs text-slate-500 mb-1">
            <Link to="/references" className="hover:text-slate-300">
              Références
            </Link>{' '}
            ›{' '}
            <Link to={`/references/${String(refId)}`} className="hover:text-slate-300">
              {refQuery.data?.reference.nom ?? '…'}
            </Link>
          </div>
          <h1 className="text-2xl font-bold text-slate-100">Rouleaux disponibles</h1>
          <p className="text-xs text-slate-500 mt-1">
            Tri FIFO (container le plus ancien en premier).
          </p>
        </div>
      </header>

      {rouleauxQuery.isLoading && <div className="text-sm text-slate-400">Chargement…</div>}
      {rouleauxQuery.isSuccess && rouleauxQuery.data.items.length === 0 && (
        <div className="bg-slate-900/40 border border-dashed border-slate-800 rounded-xl p-8 text-center text-slate-500">
          Aucun rouleau pour cette référence.
        </div>
      )}
      {rouleauxQuery.isSuccess && rouleauxQuery.data.items.length > 0 && (
        <div className="bg-slate-900/40 border border-slate-800 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-900 text-xs text-slate-400 uppercase tracking-wider">
              <tr>
                <th className="px-4 py-2 text-left">Container</th>
                <th className="px-4 py-2 text-left">Date cmd.</th>
                <th className="px-4 py-2 text-left">Largeur</th>
                <th className="px-4 py-2 text-left">Longueur</th>
                <th className="px-4 py-2 text-left">Progression</th>
                <th className="px-4 py-2 text-right">Coût €/m²</th>
                <th className="px-4 py-2 text-left">Statut</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {rouleauxQuery.data.items.map((r) => {
                const pct =
                  r.longueur_initiale_cm > 0
                    ? (r.longueur_restante_cm * 100) / r.longueur_initiale_cm
                    : 0;
                return (
                  <tr key={r.id} className="hover:bg-slate-900/60">
                    <td className="px-4 py-2 text-slate-200">{r.container_reference}</td>
                    <td className="px-4 py-2 text-xs text-slate-400">
                      {formatDate(r.container_date_commande)}
                    </td>
                    <td className="px-4 py-2 text-slate-300">{r.largeur_cm} cm</td>
                    <td className="px-4 py-2 text-slate-200">
                      {fmtM(r.longueur_restante_cm)} / {fmtM(r.longueur_initiale_cm)}
                    </td>
                    <td className="px-4 py-2 w-40">
                      <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${
                            r.statut === 'Epuise'
                              ? 'bg-red-600'
                              : r.statut === 'Entame'
                                ? 'bg-amber-500'
                                : 'bg-floryon-green'
                          }`}
                          style={{ width: `${String(pct)}%` }}
                        />
                      </div>
                    </td>
                    <td className="px-4 py-2 text-right text-slate-200">
                      {formatEuro(r.cout_eur_par_m2_centimes)}
                    </td>
                    <td className="px-4 py-2 text-xs">
                      <span
                        className={`px-2 py-0.5 rounded text-xs ${
                          r.statut === 'En_stock'
                            ? 'bg-emerald-900/40 text-emerald-300'
                            : r.statut === 'Entame'
                              ? 'bg-amber-900/40 text-amber-300'
                              : r.statut === 'Epuise'
                                ? 'bg-red-900/40 text-red-300'
                                : 'bg-slate-800 text-slate-400'
                        }`}
                      >
                        {r.statut}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
